import { ComponentPropsWithoutRef, ReactElement, ReactNode, useEffect, useRef, useState } from 'react';

import { SystemProps } from '@sternum/styled-system';
import { Tooltip, TooltipProps } from '@sternum/ui';

import { ThemeMode, ThemeProvider } from '../theme/main';

import { Container, HelperContainer, LeftRightMarkPlaceholder, Wrapper } from './TextOverflowBox.styles';

export interface TextOverflowBoxProps extends ComponentPropsWithoutRef<'div'>, Omit<SystemProps, 'color'> {
  tooltipContent?: ReactNode;
  /**
   * Display tooltip only if 3 dots are visible
   */
  displayTooltipOnlyIfNeeded?: boolean;
  tooltipProps?: TooltipProps;
  tooltipThemeMode?: ThemeMode;
  textDirection?: 'ltr' | 'rtl';
  maxTooltipWidth?: string;
}

export function TextOverflowBox({
  tooltipContent,
  tooltipProps,
  displayTooltipOnlyIfNeeded,
  tooltipThemeMode,
  textDirection,
  maxTooltipWidth,
  ...props
}: TextOverflowBoxProps) {
  const leftToRightMark = String.fromCharCode(8206);
  const mainBoxRef = useRef<HTMLDivElement | null>(null);
  const helperBoxRef = useRef<HTMLDivElement | null>(null);

  const [showTooltip, setShowTooltip] = useState<boolean | undefined>(undefined);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // COMMENT: for display tooltip only if needed this effect should be recalculated every render
    const offsetWidth = mainBoxRef.current?.offsetWidth || 0;
    const scrollWidth = helperBoxRef.current?.scrollWidth || 0;

    if (displayTooltipOnlyIfNeeded) {
      setShowTooltip(scrollWidth > offsetWidth ? undefined : false);
    }
  });

  const renderWithTheme = (renderedContent: ReactElement) => {
    if (!tooltipThemeMode) {
      return renderedContent;
    }

    return <ThemeProvider mode={tooltipThemeMode}>{renderedContent}</ThemeProvider>;
  };

  if (tooltipContent) {
    if (displayTooltipOnlyIfNeeded) {
      return renderWithTheme(
        <Tooltip
          title={tooltipContent}
          position="top"
          useWrapper={false}
          show={displayTooltipOnlyIfNeeded ? showTooltip : undefined}
          {...tooltipProps}
        >
          <Wrapper maxWidth={maxTooltipWidth}>
            <Container dir={textDirection} ref={mainBoxRef} {...props}>
              {textDirection === 'rtl' && <LeftRightMarkPlaceholder>{leftToRightMark}</LeftRightMarkPlaceholder>}
              {props.children}
            </Container>
            <HelperContainer ref={helperBoxRef}>{props.children}</HelperContainer>
          </Wrapper>
        </Tooltip>,
      );
    } else {
      return renderWithTheme(
        <Tooltip title={tooltipContent} position="top" {...tooltipProps}>
          <Container dir={textDirection} {...props}>
            {textDirection === 'rtl' && <LeftRightMarkPlaceholder>{leftToRightMark}</LeftRightMarkPlaceholder>}
            {props.children}
          </Container>
        </Tooltip>,
      );
    }
  }

  return <Container {...props} />;
}
